import React, { useRef } from 'react';

interface OtpCodeInputProps {
  /** Current code value (digits only). */
  value: string;
  /** Called with the new code on every change/paste. */
  onChange: (code: string) => void;
  /** Number of digit boxes. Defaults to 6. */
  length?: number;
  /** Focus the first box on mount. */
  autoFocus?: boolean;
  disabled?: boolean;
}

/**
 * Shared 6-box OTP entry used everywhere a verification code is typed (login, register, and the
 * onboarding email-change screen). Handles auto-advance, backspace-to-previous, and paste. The
 * code string is the single source of truth — the parent owns it via `value`/`onChange`.
 */
const OtpCodeInput: React.FC<OtpCodeInputProps> = ({ value, onChange, length = 6, autoFocus, disabled }) => {
  const refs = useRef<(HTMLInputElement | null)[]>([]);
  const digits = Array.from({ length }, (_, i) => value[i] ?? '');

  const setAt = (index: number, digit: string) => {
    const arr = digits.slice();
    arr[index] = digit;
    onChange(arr.join(''));
  };

  const handleChange = (index: number, raw: string) => {
    const digit = raw.replace(/\D/g, '').slice(-1);
    setAt(index, digit);
    if (digit && index < length - 1) refs.current[index + 1]?.focus();
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !digits[index] && index > 0) {
      refs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    const paste = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, length);
    if (paste.length) {
      onChange(paste);
      refs.current[Math.min(paste.length, length - 1)]?.focus();
    }
    e.preventDefault();
  };

  return (
    <div className="flex gap-2 justify-center">
      {digits.map((digit, i) => (
        <input
          key={i}
          ref={(el) => { refs.current[i] = el; }}
          type="text"
          inputMode="numeric"
          maxLength={1}
          value={digit}
          disabled={disabled}
          autoFocus={autoFocus && i === 0}
          onChange={(e) => handleChange(i, e.target.value)}
          onKeyDown={(e) => handleKeyDown(i, e)}
          onPaste={handlePaste}
          className="w-11 h-14 border border-ui-border rounded-lg text-center text-h3 text-ui-text bg-ui-input focus:outline-none focus:border-ui-ring focus:ring-2 focus:ring-ui-ring/20 transition disabled:opacity-50"
        />
      ))}
    </div>
  );
};

export default OtpCodeInput;
